import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Target, Plus, MapPin, Calendar, Eye, Edit, Download, TrendingUp, Award, Palette, BarChart3, Users, ChevronRight, DollarSign, MousePointer, Clock, Zap, Activity, PieChart, Search, Building2, User, Phone, Mail, Monitor, Trash2, Loader2, Archive, ArchiveRestore, AlertCircle, RotateCcw, Landmark, Copy, ShieldCheck, UserCheck } from "lucide-react";
import { BANK_IBAN } from "@/components/campaign/utils/constants";
import { Campaign } from "../types";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CampaignCreativeEditor } from "./CampaignCreativeEditor";
import { exportToCsv, exportToPdf, exportSingleCampaignToPdf } from "../utils/exportUtils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import GroupedCreativesDisplay from "@/components/ui/GroupedCreativesDisplay";
import CreativeScheduleTimeline from "@/components/ui/CreativeScheduleTimeline";

interface CampaignsTabProps {
  campaigns: Campaign[];
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  onRefresh: () => void;
}

export const CampaignsTab = ({ campaigns, getStatusColor, getStatusText, onRefresh }: CampaignsTabProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingCampaign, setDeletingCampaign] = useState<string | null>(null);
  const [archivingCampaign, setArchivingCampaign] = useState<string | null>(null);
  const [resubmittingCampaign, setResubmittingCampaign] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const handleResubmitCampaign = async (campaign: Campaign) => {
    setResubmittingCampaign(campaign.id);
    try {
      // Update campaign status to pending and clear rejection reason
      const { error } = await supabase
        .from('saved_campaigns')
        .update({ 
          status: 'pending',
          rejection_reason: null
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error resubmitting campaign:', error);
        toast.error(language === 'ar' ? 'فشل في إعادة تقديم الحملة' : 'Failed to resubmit campaign');
        return;
      }

      toast.success(language === 'ar' ? 'تم إعادة تقديم الحملة بنجاح' : 'Campaign resubmitted successfully');
      setSelectedCampaign(null);
      onRefresh();
    } catch (error) {
      console.error('Error resubmitting campaign:', error);
      toast.error(language === 'ar' ? 'فشل في إعادة تقديم الحملة' : 'Failed to resubmit campaign');
    } finally {
      setResubmittingCampaign(null);
    }
  };

  const handleEditAndResubmit = (campaign: Campaign) => {
    // Navigate to campaign edit page
    navigate(`/campaign/edit/${campaign.id}`);
  };

  const handleArchiveCampaign = async (campaignId: string, archive: boolean) => {
    setArchivingCampaign(campaignId);
    try {
      const { error } = await supabase
        .from('saved_campaigns')
        .update({ is_archived: archive })
        .eq('id', campaignId);

      if (error) {
        console.error('Error archiving campaign:', error);
        toast.error(language === 'ar' ? 'فشل في أرشفة الحملة' : 'Failed to archive campaign');
        return;
      }

      toast.success(
        archive 
          ? (language === 'ar' ? 'تم أرشفة الحملة بنجاح' : 'Campaign archived successfully')
          : (language === 'ar' ? 'تم استعادة الحملة بنجاح' : 'Campaign restored successfully')
      );
      setSelectedCampaign(null);
      onRefresh();
    } catch (error) {
      console.error('Error archiving campaign:', error);
      toast.error(language === 'ar' ? 'فشل في أرشفة الحملة' : 'Failed to archive campaign');
    } finally {
      setArchivingCampaign(null);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    setDeletingCampaign(campaignId);
    try {
      const { error } = await supabase
        .from('saved_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) {
        console.error('Error deleting campaign:', error);
        toast.error(language === 'ar' ? 'فشل في حذف الحملة' : 'Failed to delete campaign');
        return;
      }

      toast.success(language === 'ar' ? 'تم حذف الحملة بنجاح' : 'Campaign deleted successfully');
      setSelectedCampaign(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(language === 'ar' ? 'فشل في حذف الحملة' : 'Failed to delete campaign');
    } finally {
      setDeletingCampaign(null);
    }
  };

  const handleEditCreatives = (campaign: Campaign) => {
    setEditingCampaign(campaign);
  };

  const handleCampaignClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} ${language === 'ar' ? 'ريال' : 'SAR'}`;
  };

  const calculateCTR = (campaign: Campaign) => {
    return (campaign.engagement * 0.1).toFixed(2);
  };

  const calculateCPM = (campaign: Campaign) => {
    return ((campaign.spent / campaign.reach) * 1000).toFixed(2);
  };

  const calculateROI = (campaign: Campaign) => {
    const roi = ((campaign.reach * campaign.engagement * 0.001 - campaign.spent) / campaign.spent) * 100;
    return roi.toFixed(1);
  };

  const handleExport = (type: 'csv' | 'pdf') => {
    if (campaigns.length === 0) {
      toast.error(language === 'ar' ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }

    try {
      if (type === 'csv') {
        exportToCsv(campaigns, language);
        toast.success(language === 'ar' ? 'تم تصدير البيانات بنجاح' : 'Data exported successfully');
      } else {
        exportToPdf(campaigns, language);
        toast.success(language === 'ar' ? 'تم إنشاء التقرير بنجاح' : 'Report generated successfully');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'ar' ? 'فشل في تصدير البيانات' : 'Failed to export data');
    }
  };

  const handleExportSingleCampaign = (campaign: Campaign) => {
    try {
      exportSingleCampaignToPdf(campaign, language);
      toast.success(language === 'ar' ? 'تم إنشاء التقرير بنجاح' : 'Report generated successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'ar' ? 'فشل في تصدير البيانات' : 'Failed to export data');
    }
  };

  // Filter campaigns based on search term and archive status
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArchiveFilter = showArchived ? campaign.is_archived : !campaign.is_archived;
    return matchesSearch && matchesArchiveFilter;
  });

  const archivedCount = campaigns.filter(c => c.is_archived).length;

  return (
    <div className="space-y-6">
      {/* Search Bar and Archive Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={language === 'ar' ? 'البحث عن حملة بالاسم أو المعرف...' : 'Search campaigns by name or ID...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-background/60 backdrop-blur-sm border-border/60"
          />
        </div>
        {archivedCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="show-archived" className="text-sm cursor-pointer flex items-center gap-2">
              <Archive className="h-4 w-4" />
              {language === 'ar' ? `المؤرشفة (${archivedCount})` : `Archived (${archivedCount})`}
            </Label>
          </div>
        )}
      </div>
      {/* Recent Campaign Performance */}
      <Card className="bg-card/80 backdrop-blur-sm border shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <BarChart3 className="h-6 w-6 text-primary-foreground" />
              </div>
              {language === 'ar' ? 'أداء الحملات الحديثة' : 'Recent Campaign Performance'}
            </CardTitle>
            {campaigns.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('csv')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('pdf')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <Target className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 
                  (language === 'ar' ? 'لم يتم العثور على حملات' : 'No campaigns found') :
                  (language === 'ar' ? 'لم يتم إنشاء أي حملة بعد' : 'No campaigns created yet')
                }
              </p>
              {!searchTerm && (
                <Button onClick={() => navigate('/campaign')} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'ar' ? 'إنشاء حملة جديدة' : 'Create Your First Campaign'}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.slice(0, 3).map((campaign, index) => (
                <Dialog key={campaign.id} open={selectedCampaign?.id === campaign.id} onOpenChange={(open) => !open && setSelectedCampaign(null)}>
                  <DialogTrigger asChild>
                    <div
                      onClick={() => handleCampaignClick(campaign)}
                      className="p-6 rounded-lg border bg-card hover:bg-accent/50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{campaign.name}</h3>
                            <Badge className={getStatusColor(campaign.status)}>
                              {getStatusText(campaign.status)}
                            </Badge>
                          </div>
                          {/* Approval Status Badges */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`text-xs gap-1 ${
                              campaign.status === 'approved' ? 'border-green-500/50 text-green-700 dark:text-green-400' :
                              campaign.status === 'rejected' ? 'border-destructive/50 text-destructive' :
                              'border-yellow-500/50 text-yellow-700 dark:text-yellow-400'
                            }`}>
                              <ShieldCheck className="h-3 w-3" />
                              {language === 'ar' ? 'الإدارة:' : 'Admin:'} {getStatusText(campaign.status)}
                            </Badge>
                            {(() => {
                              const approvals = campaign.mediaOwnerApprovals || [];
                              const allApproved = approvals.length > 0 && approvals.every(a => a.status === 'approved');
                              const anyRejected = approvals.some(a => a.status === 'rejected');
                              const moStatus = allApproved ? 'approved' : anyRejected ? 'rejected' : 'pending';
                              return (
                                <Badge variant="outline" className={`text-xs gap-1 ${
                                  moStatus === 'approved' ? 'border-green-500/50 text-green-700 dark:text-green-400' :
                                  moStatus === 'rejected' ? 'border-destructive/50 text-destructive' :
                                  'border-yellow-500/50 text-yellow-700 dark:text-yellow-400'
                                }`}>
                                  <UserCheck className="h-3 w-3" />
                                  {language === 'ar' ? 'مالك الوسائط:' : 'Media Owner:'} {getStatusText(moStatus)}
                                </Badge>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {campaign.locations} {language === 'ar' ? 'موقع' : 'locations'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {campaign.status === 'approved' ? (language === 'ar' ? 'نشطة' : 'Active') : 
                               campaign.status === 'pending' ? (language === 'ar' ? 'قيد المراجعة' : 'Under Review') :
                               campaign.status === 'rejected' ? (language === 'ar' ? 'مرفوضة' : 'Rejected') :
                               (language === 'ar' ? 'غير معروف' : 'Unknown')}
                            </span>
                          </div>
                          {campaign.status === 'approved' ? (
                            <div className="grid grid-cols-3 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الميزانية' : 'Budget'}</p>
                                <p className="font-semibold">{formatCurrency(campaign.budget)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الوصول' : 'Reach'}</p>
                                <p className="font-semibold">{(campaign.reach / 1000).toFixed(1)}K</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">{language === 'ar' ? 'التفاعل' : 'Engagement'}</p>
                                <p className="font-semibold">{campaign.engagement}%</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground mt-2">
                              {language === 'ar' ? 'ستتوفر بيانات الأداء بعد انتهاء الحملة' : 'Performance data will be available after the campaign ends'}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <DialogTitle className="text-2xl">{campaign.name}</DialogTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {campaign.brandName && `${language === 'ar' ? 'العلامة التجارية:' : 'Brand:'} ${campaign.brandName}`}
                          </p>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                          <Badge className={getStatusColor(campaign.status)}>
                            {getStatusText(campaign.status)}
                          </Badge>
                          {campaign.status === 'rejected' && campaign.rejection_reason && (
                            <Badge variant="outline" className="gap-1 border-destructive text-destructive">
                              {language === 'ar' ? 'يوجد سبب للرفض' : 'Has Rejection Reason'}
                            </Badge>
                          )}
                          {campaign.is_archived && (
                            <Badge variant="secondary" className="gap-1">
                              <Archive className="h-3 w-3" />
                              {language === 'ar' ? 'مؤرشفة' : 'Archived'}
                            </Badge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportSingleCampaign(campaign)}
                            className="gap-2"
                          >
                            <Download className="h-4 w-4" />
                            {language === 'ar' ? 'تصدير' : 'Export'}
                          </Button>
                          
                          {/* Archive/Restore Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArchiveCampaign(campaign.id, !campaign.is_archived)}
                            disabled={archivingCampaign === campaign.id}
                            className="gap-2"
                          >
                            {archivingCampaign === campaign.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : campaign.is_archived ? (
                              <>
                                <ArchiveRestore className="h-4 w-4" />
                                {language === 'ar' ? 'استعادة' : 'Restore'}
                              </>
                            ) : (
                              <>
                                <Archive className="h-4 w-4" />
                                {language === 'ar' ? 'أرشفة' : 'Archive'}
                              </>
                            )}
                          </Button>

                          {/* Delete Button */}
                          <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  {language === 'ar' ? 'حذف نهائي' : 'Delete'}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {language === 'ar' ? 'حذف الحملة نهائياً' : 'Permanently Delete Campaign'}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {language === 'ar' 
                                      ? 'هل أنت متأكد من حذف هذه الحملة نهائياً؟ لا يمكن التراجع عن هذا الإجراء.'
                                      : 'Are you sure you want to permanently delete this campaign? This action cannot be undone.'}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>
                                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteCampaign(campaign.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    disabled={deletingCampaign === campaign.id}
                                  >
                                    {deletingCampaign === campaign.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      language === 'ar' ? 'حذف نهائي' : 'Delete Permanently'
                                    )}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                        </div>
                      </div>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      {/* Approval Status Breakdown */}
                      <div className="bg-muted/30 border border-border/50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-primary" />
                          {language === 'ar' ? 'حالة الموافقة' : 'Approval Status'}
                        </h4>
                        <div className="space-y-2">
                          {/* Admin Approval */}
                          <div className="flex items-center justify-between py-1.5 px-3 rounded-md bg-background/60">
                            <span className="text-sm font-medium">{language === 'ar' ? 'موافقة الإدارة' : 'Admin Approval'}</span>
                            <Badge variant="outline" className={`text-xs ${
                              campaign.status === 'approved' ? 'border-green-500/50 text-green-700 dark:text-green-400' :
                              campaign.status === 'rejected' ? 'border-destructive/50 text-destructive' :
                              'border-yellow-500/50 text-yellow-700 dark:text-yellow-400'
                            }`}>
                              {getStatusText(campaign.status)}
                            </Badge>
                          </div>
                          {/* Media Owner Approvals */}
                          {(campaign.mediaOwnerApprovals || []).map((approval, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-3 rounded-md bg-background/60">
                              <span className="text-sm font-medium flex items-center gap-1.5">
                                <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
                                {approval.media_owner_name}
                              </span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs ${
                                  approval.status === 'approved' ? 'border-green-500/50 text-green-700 dark:text-green-400' :
                                  approval.status === 'rejected' ? 'border-destructive/50 text-destructive' :
                                  'border-yellow-500/50 text-yellow-700 dark:text-yellow-400'
                                }`}>
                                  {getStatusText(approval.status)}
                                </Badge>
                                {approval.status === 'rejected' && approval.rejection_reason && (
                                  <span className="text-xs text-destructive/70 max-w-[200px] truncate" title={approval.rejection_reason}>
                                    {approval.rejection_reason}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Rejection Reason Alert with Resubmit Options */}
                      {campaign.status === 'rejected' && (
                        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-destructive">
                                {language === 'ar' ? 'سبب الرفض' : 'Rejection Reason'}
                              </h4>
                              {campaign.rejection_reason ? (
                                <p className="text-sm text-destructive/80 mt-1">
                                  {campaign.rejection_reason}
                                </p>
                              ) : (
                                <p className="text-sm text-destructive/80 mt-1 italic">
                                  {language === 'ar' ? 'لم يتم تقديم سبب' : 'No reason provided'}
                                </p>
                              )}
                              
                              {/* Resubmit Actions */}
                              <div className="flex flex-wrap gap-2 mt-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditAndResubmit(campaign)}
                                  className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                                >
                                  <Edit className="h-4 w-4" />
                                  {language === 'ar' ? 'تعديل وإعادة التقديم' : 'Edit & Resubmit'}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="default"
                                      size="sm"
                                      className="gap-2"
                                      disabled={resubmittingCampaign === campaign.id}
                                    >
                                      {resubmittingCampaign === campaign.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RotateCcw className="h-4 w-4" />
                                      )}
                                      {language === 'ar' ? 'إعادة التقديم مباشرة' : 'Resubmit Now'}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        {language === 'ar' ? 'إعادة تقديم الحملة' : 'Resubmit Campaign'}
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {language === 'ar' 
                                          ? 'هل أنت متأكد من إعادة تقديم هذه الحملة للمراجعة؟ سيتم مسح سبب الرفض وتغيير الحالة إلى قيد المراجعة.'
                                          : 'Are you sure you want to resubmit this campaign for review? The rejection reason will be cleared and status will change to pending.'}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleResubmitCampaign(campaign)}
                                        disabled={resubmittingCampaign === campaign.id}
                                      >
                                        {resubmittingCampaign === campaign.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          language === 'ar' ? 'إعادة التقديم' : 'Resubmit'
                                        )}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Campaign Details Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Company & Contact Info */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-primary" />
                              {language === 'ar' ? 'معلومات الشركة' : 'Company Information'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {campaign.companyName && (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{campaign.companyName}</span>
                              </div>
                            )}
                            {campaign.personInCharge && (
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{campaign.personInCharge}</span>
                              </div>
                            )}
                            {campaign.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{campaign.email}</span>
                              </div>
                            )}
                            {campaign.mobile && (
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{campaign.mobile}</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* Campaign Timing */}
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-primary" />
                              {language === 'ar' ? 'توقيت الحملة' : 'Campaign Timing'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{language === 'ar' ? 'تاريخ البدء' : 'Start Date'}</span>
                              <span className="text-sm font-medium">{campaign.startDate}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{language === 'ar' ? 'تاريخ الانتهاء' : 'End Date'}</span>
                              <span className="text-sm font-medium">{campaign.endDate}</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{language === 'ar' ? 'الميزانية' : 'Budget'}</span>
                              <span className="text-sm font-medium">{formatCurrency(campaign.budget)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">{language === 'ar' ? 'نوع الدفع' : 'Payment Type'}</span>
                              <span className="text-sm font-medium capitalize">{campaign.payment_type || 'Immediate'}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Bank Transfer Details Section - Only show for bank transfer payments */}
                      {(campaign.payment_type === 'bank_transfer' || campaign.payment_type === 'deferred') && (
                        <Card className="border-amber-200 bg-amber-50/50">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Landmark className="h-4 w-4 text-amber-600" />
                              {language === 'ar' ? 'تفاصيل التحويل البنكي' : 'Bank Transfer Details'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="bg-card dark:bg-card p-4 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{language === 'ar' ? 'رقم الآيبان' : 'IBAN'}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-mono font-medium">{BANK_IBAN}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => {
                                      navigator.clipboard.writeText(BANK_IBAN);
                                      toast.success(language === 'ar' ? 'تم نسخ الآيبان' : 'IBAN copied to clipboard');
                                    }}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                              <Separator />
                              <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">{language === 'ar' ? 'اسم البنك' : 'Bank Name'}</span>
                                <span className="text-sm font-medium">Alrajhi Bank</span>
                              </div>
                              <Separator />
                              <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">{language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}</span>
                                <span className="text-sm font-medium">Sales@Fusion-Media.sa</span>
                              </div>
                            </div>
                            <p className="text-xs text-amber-700 bg-amber-100 p-2 rounded">
                              {language === 'ar' 
                                ? 'يرجى تحويل المبلغ إلى الحساب البنكي أعلاه وإرسال إيصال الدفع إلى البريد الإلكتروني. ستبدأ حملتك بعد تأكيد استلام الدفع.'
                                : 'Please transfer the amount to the bank account above and send the payment receipt to the email address. Your campaign will start after payment confirmation.'
                              }
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      {/* Locations Section */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary" />
                            {language === 'ar' ? 'المواقع والشاشات' : 'Locations & Screens'}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <p className="text-xs text-muted-foreground">{language === 'ar' ? 'مالك الوسائط' : 'Media Owner'}</p>
                              <p className="font-medium">{campaign.mediaOwnerName || '-'}</p>
                            </div>
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الموقع' : 'Location'}</p>
                              <p className="font-medium">{campaign.locationName || '-'}</p>
                            </div>
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <p className="text-xs text-muted-foreground">{language === 'ar' ? 'عدد الشاشات' : 'Total Screens'}</p>
                              <p className="font-medium">{campaign.locations}</p>
                            </div>
                          </div>
                          
                          {/* Locations List */}
                          <div className="space-y-2 mt-4">
                            <p className="text-sm font-medium">
                              {language === 'ar' ? 'قائمة المواقع:' : 'Locations List:'}
                            </p>
                            {campaign.selectedLocations && campaign.selectedLocations.length > 0 ? (
                              <div className="grid gap-2 max-h-60 overflow-y-auto">
                                {campaign.selectedLocations.map((loc, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
                                    <div className="flex items-center gap-3">
                                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Monitor className="h-4 w-4 text-primary" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium">{loc.name || 'Unknown Location'}</p>
                                        <p className="text-xs text-muted-foreground">{loc.mediaOwner || '-'}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                      <Badge variant="secondary">{loc.region || '-'}</Badge>
                                      <Badge variant="outline">{loc.devices || 0} {language === 'ar' ? 'شاشة' : 'screens'}</Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">
                                {language === 'ar' ? 'لا توجد مواقع محددة' : 'No locations specified'}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Schedule Timeline View */}
                      {campaign.creative_urls && campaign.creative_urls.length > 0 && campaign.creative_metadata && campaign.creative_metadata.length > 0 && (
                        <CreativeScheduleTimeline
                          creativeUrls={campaign.creative_urls}
                          creativeMetadata={campaign.creative_metadata}
                          startDate={campaign.startDate}
                          endDate={campaign.endDate}
                          language={language}
                          selectedLocations={campaign.selectedLocations}
                        />
                      )}

                      {/* Creative Materials */}
                      {campaign.creative_urls && campaign.creative_urls.length > 0 && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Palette className="h-4 w-4 text-primary" />
                              {language === 'ar' ? 'المواد الإبداعية' : 'Creative Materials'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <GroupedCreativesDisplay
                              creativeUrls={campaign.creative_urls || []}
                              creativeMetadata={campaign.creative_metadata}
                              language={language}
                              startDate={campaign.startDate}
                              durationDays={campaign.startDate && campaign.endDate ? 
                                Math.ceil((new Date(campaign.endDate).getTime() - new Date(campaign.startDate).getTime()) / (1000 * 60 * 60 * 24)) : undefined
                              }
                              showGroupHeaders={true}
                              showScheduledDays={true}
                              showDetailedView={true}
                              selectedLocations={campaign.selectedLocations}
                            />
                          </CardContent>
                        </Card>
                      )}

                      {/* Performance Section - Only for completed campaigns */}
                      {campaign.status === 'completed' && campaign.reach > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-4">
                            <h3 className="font-semibold flex items-center gap-2">
                              <BarChart3 className="h-4 w-4 text-primary" />
                              {language === 'ar' ? 'أداء الحملة' : 'Campaign Performance'}
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <Card>
                                <CardContent className="pt-6">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                      <Users className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الوصول' : 'Reach'}</p>
                                      <p className="text-xl font-bold">{(campaign.reach / 1000).toFixed(1)}K</p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-6">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                                      <MousePointer className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{language === 'ar' ? 'التفاعل' : 'Engagement'}</p>
                                      <p className="text-xl font-bold">{campaign.engagement}%</p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-6">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                      <DollarSign className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{language === 'ar' ? 'الإنفاق' : 'Spent'}</p>
                                      <p className="text-xl font-bold">{formatCurrency(campaign.spent)}</p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="pt-6">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                                      <TrendingUp className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{language === 'ar' ? 'التكلفة لكل ألف' : 'CPM'}</p>
                                      <p className="text-xl font-bold">{calculateCPM(campaign)}</p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Campaigns List */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">
          {language === 'ar' ? 'جميع الحملات' : 'All Campaigns'}
        </h2>
        <Button onClick={() => navigate('/campaign')} size="lg" className="shadow-lg">
          <Plus className="h-4 w-4 mr-2" />
          {language === 'ar' ? 'حملة جديدة' : 'New Campaign'}
        </Button>
      </div>

      <div className="grid gap-6">
        {filteredCampaigns.length === 0 ? (
          <Card className="bg-card/80 backdrop-blur-sm border shadow-lg">
            <CardContent className="text-center py-20 space-y-6">
              <div className="h-24 w-24 mx-auto rounded-full bg-gradient-to-br from-muted to-muted/80 flex items-center justify-center shadow-md">
                <Target className="h-12 w-12 text-muted-foreground" />
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  {language === 'ar' ? 'لا توجد حملات' : 'No Campaigns Yet'}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                  {language === 'ar' 
                    ? 'ابدأ بإنشاء حملتك الأولى للوصول إلى جمهورك المستهدف وتحقيق أهدافك التسويقية'
                    : 'Start by creating your first campaign to reach your target audience and achieve your marketing goals'}
                </p>
              </div>
              <Button onClick={() => navigate('/campaign')} size="lg" className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'إنشاء حملة جديدة' : 'Create New Campaign'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredCampaigns.map((campaign) => (
        <Card className="bg-card/80 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <Target className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-3 text-xl">
                    {campaign.name}
                    <Badge className={`${getStatusColor(campaign.status)} border-0 shadow-sm`}>
                      {getStatusText(campaign.status)}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {campaign.locations} {language === 'ar' ? 'موقع' : 'locations'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {campaign.status === 'approved' ? (language === 'ar' ? 'نشطة' : 'Active') : 
                       campaign.status === 'awaiting_review' ? (language === 'ar' ? 'قيد المراجعة' : 'Under Review') :
                       (language === 'ar' ? 'متوقفة' : 'Stopped')}
                    </span>
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                  onClick={() => handleCampaignClick(campaign)}
                  title={language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                  onClick={() => handleEditCreatives(campaign)}
                  title={language === 'ar' ? 'تعديل المواد الإبداعية' : 'Edit Creatives'}
                >
                  <Palette className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaign/edit/${campaign.id}`);
                  }}
                  title={language === 'ar' ? 'تعديل الحملة' : 'Edit Campaign'}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                  onClick={() => handleExportSingleCampaign(campaign)}
                  title={language === 'ar' ? 'تنزيل التقرير' : 'Download Report'}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      {language === 'ar' ? 'الميزانية' : 'Budget'}
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {campaign.budget.toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                    </p>
                    <Progress 
                      value={(campaign.spent / campaign.budget) * 100} 
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'ar' ? 'تم إنفاق' : 'Spent'} {campaign.spent.toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                    </p>
                  </div>
                  {campaign.status === 'completed' ? (
                    <>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          {language === 'ar' ? 'الوصول' : 'Reach'}
                        </p>
                        <p className="text-2xl font-bold text-foreground">
                          {(campaign.reach / 1000).toLocaleString()}K
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          {language === 'ar' ? 'التفاعل' : 'Engagement'}
                        </p>
                        <p className="text-2xl font-bold text-foreground">{campaign.engagement}%</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          {language === 'ar' ? 'التكلفة لكل ألف' : 'CPM'}
                        </p>
                        <p className="text-2xl font-bold text-foreground">
                          {campaign.reach > 0 ? ((campaign.spent / campaign.reach) * 1000).toFixed(2) : '0'} {language === 'ar' ? 'ر.س' : 'SAR'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'ar' ? 'تكلفة فعالة' : 'Cost effective'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-3 text-center py-4">
                      <p className="text-sm text-muted-foreground">
                        {language === 'ar' 
                          ? 'ستتوفر بيانات الأداء (الوصول، التفاعل، التكلفة لكل ألف) بعد انتهاء الحملة' 
                          : 'Performance data (Reach, Engagement, CPM) will be available after the campaign ends'}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Creative Editor Dialog */}
      {editingCampaign && (
        <CampaignCreativeEditor
          campaign={editingCampaign}
          isOpen={!!editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onUpdate={onRefresh}
        />
      )}
    </div>
  );
};
